import React, { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { toast } from 'react-hot-toast';
import { dataImportService } from '@/services/data-import.service';

interface UploadPortfolioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

export function UploadPortfolioModal({
  isOpen,
  onClose,
  onUploadSuccess,
}: UploadPortfolioModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const validateFile = (file: File): boolean => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];

    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid Excel (.xls, .xlsx) or CSV file');
      return false;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);

    try {
      const result = await dataImportService.importLoans(selectedFile, {
        onProgress: (percent) => {
          console.log('Upload progress:', percent);
        },
      });

      if (result.success) {
        const successMessage =
          result.imported_count > 0
            ? `Successfully imported ${result.imported_count} loan${
                result.imported_count > 1 ? 's' : ''
              }`
            : 'Upload completed';

        toast.success(successMessage);

        if (result.failed_count > 0) {
          toast.error(
            `${result.failed_count} loan${
              result.failed_count > 1 ? 's' : ''
            } failed to import`,
            { duration: 5000 }
          );
        }

        if (result.warnings && result.warnings.length > 0) {
          result.warnings.forEach((warning) => {
            toast(warning, { icon: '⚠️' });
          });
        }

        onUploadSuccess?.();
        handleClose();
      } else {
        toast.error('Failed to upload portfolio. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload portfolio. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsDragging(false);
    onClose();
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Portfolio"
      description="Upload your loan portfolio from an Excel or CSV file"
    >
      <div>
        <div className="px-6 py-6">
          {/* Drag and Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={triggerFileInput}
            data-test-id="drag-drop-area"
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              data-test-id="file-input"
            />

            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-gray-100 rounded-full">
                <Upload className="h-8 w-8 text-gray-600" />
              </div>

              <div>
                <p
                  className="text-lg font-medium text-gray-900"
                  data-test-id="upload-instruction"
                >
                  {isDragging
                    ? 'Drop your file here'
                    : 'Drag & drop your portfolio file here'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  or{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFileInput();
                    }}
                    data-test-id="browse-files-button"
                  >
                    browse files
                  </button>
                </p>
              </div>

              <p
                className="text-xs text-gray-500"
                data-test-id="file-requirements"
              >
                Supported formats: .xlsx, .xls, .csv (max 10MB)
              </p>
            </div>
          </div>

          {/* Selected File Display */}
          {selectedFile && (
            <div
              className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between"
              data-test-id="selected-file-container"
            >
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-blue-600" />
                <div>
                  <p
                    className="font-medium text-gray-900"
                    data-test-id="selected-file-name"
                  >
                    {selectedFile.name}
                  </p>
                  <p
                    className="text-sm text-gray-500"
                    data-test-id="selected-file-size"
                  >
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-gray-400 hover:text-gray-600"
                data-test-id="remove-file-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Instructions */}
          <div
            className="mt-6 p-4 bg-blue-50 rounded-lg"
            data-test-id="upload-instructions"
          >
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">File format requirements:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>First row must contain column headers</li>
                  <li>
                    Required columns: Loan ID, Loan Name, Borrower, Balance,
                    Property Type
                  </li>
                  <li>Date fields should be in MM/DD/YYYY format</li>
                  <li>Numeric fields should not contain currency symbols</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            data-test-id="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="flex items-center gap-2"
            data-test-id="upload-button"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload Portfolio
              </>
            )}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
